import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';
import { CameraDevice } from 'html5-qrcode/esm/camera/core';
import { QrcodeSuccessCallback } from 'html5-qrcode/esm/core';
import { useCallback, useEffect, useState } from 'react';
import useWidth from '../../../../../components/Hooks/useWidth';
import storage from '../../../../../hooks/useStorage/useStorage';
import LangEnum from '../../../../../lang/LangEnum';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../translation/TranslationContext';
import Html5QrcodePlugin from './Html5QrcodePlugin';

interface BarcodeReaderDialogContentProps {
  onSuccess: QrcodeSuccessCallback;
}

const DEFAULT_CAMERA_HEIGHT = 100;
const DEFAULT_CAMERA_WIDTH = 320;

const BarcodeReaderDialogContent = ({ onSuccess }: BarcodeReaderDialogContentProps) => {
  const { mediumDownWidth } = useWidth();
  const { translate } = useTranslation();

  const { team } = useApplicationContext();
  const sessionId = team?.id || 'anonymous';

  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [currentCameraId, setCurrentCameraId] = useState<string>('');

  const getAvailableCamera = useCallback(async () => {
    setIsLoadingCameras(true);
    const deviceCameras = await Html5Qrcode.getCameras();
    setIsLoadingCameras(false);
    setCameras(deviceCameras);
    const storageCameraId = storage.getCameraId(sessionId)();
    if (storageCameraId && deviceCameras.find((device) => device.id === storageCameraId)) {
      setCurrentCameraId(storageCameraId);
    }
  }, [sessionId]);

  useEffect(() => {
    getAvailableCamera();
  }, [getAvailableCamera]);

  const handleChangeCamera = (event: SelectChangeEvent<any>) => {
    setCurrentCameraId(event.target.value);
    storage.setCameraId(event.target.value, sessionId);
  };

  const CameraSelect = () => (
    <FormControl size="small" variant="outlined" fullWidth>
      <InputLabel>{translate(LangEnum.GLOBAL_CAMERA)}</InputLabel>
      <Select value={currentCameraId} onChange={handleChangeCamera} label="Câmera">
        <MenuItem value="">
          <em>{translate(LangEnum.GLOBAL_FEMALE_NONE)}</em>
        </MenuItem>
        {cameras.map((camera) => (
          <MenuItem key={camera.id} value={camera.id}>
            {camera.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      <Collapse in={isLoadingCameras}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: 2,
          }}
        >
          <CircularProgress />
        </Box>
      </Collapse>
      <Collapse in={!isLoadingCameras && !currentCameraId && !!cameras.length}>
        <Box
          sx={{
            paddingX: 2,
            paddingBottom: 2,
          }}
        >
          <Grid container>
            <Grid size={12}>
              <Typography gutterBottom>{translate(LangEnum.GLOBAL_SELECT_A_CAMERA)}</Typography>
            </Grid>
            <Grid size={12}>
              <CameraSelect />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
      <Collapse in={!!currentCameraId}>
        <>
          <Box
            sx={{
              paddingX: 2,
              paddingBottom: 1,
            }}
          >
            <CameraSelect />
          </Box>
          <Html5QrcodePlugin
            height={mediumDownWidth ? 120 : DEFAULT_CAMERA_HEIGHT}
            width={mediumDownWidth ? 400 : DEFAULT_CAMERA_WIDTH}
            onSuccess={onSuccess}
            cameraId={currentCameraId}
          />
        </>
      </Collapse>
    </>
  );
};
export default BarcodeReaderDialogContent;
